<template>
  <div class="modal fade" :id="id" data-backdrop="static" tabindex="-1" role="dialog" aria-labelledby="staticBackdrop" aria-hidden="true">
    <div class="modal-dialog modal-lg" role="document">
      <form @submit.prevent="downloadExpenseFile" class="form">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('expenses.file') }}</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" @click="closeModal">
              <span class="svg-icon svg-icon-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)"
                    fill="currentColor" />
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                    fill="currentColor" />
                </svg>
              </span>
            </div>
          </div>

          <div class="modal-body">
            <div class="row">
                <div class="col-md-4">
                    <div class="form-group row align-items-center">
                        <label class="col-form-label">{{$t("expenses.status.title")}}</label>
                        <div class="col-md-12">
                            <div class="checkbox-list">
                              <CheckboxGroupInput id="status" :options="STAUTUS_OPTION" v-model="formData.status"/>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-8">

                    <div class="row">
                      <div class="col-6">
                        <InputDate 
                          name="start_date"
                          :label="$t('expenses.filter.start_date')"
                          :inputClass="'form-control form-control-solid'"
                          :isRequired="false"
                          :placeholder="$t('expenses.filter.date')"
                          v-model="formData.startDate"
                         />
                      </div>
                      <div class="col-6">
                        <InputDate 
                          name="end_date"
                          :label="$t('expenses.filter.end_date')"
                          :inputClass="'form-control form-control-solid'"
                          :isRequired="false" 
                          :minDate="formData.startDate"
                          :placeholder="$t('expenses.filter.date')"
                          v-model="formData.endDate"
                         />
                      </div>
                    </div>

                    <div class="separator separator-solid mb-4"></div>

                    <SwitchInput id="allOffice" :label="$t('expenses.filter.allOffice')" v-model="formData.allOffices"/>


                    <div v-if="!formData.allOffices"> 
                      <Select2
                        :is-required="true"
                        :parent-id="'#'+id"
                        container-class="col-12 mt-2 mb-2"
                        :multiple="true"
                        :show-search="true"
                        :selectClass="'form-select form-control-lg form-select-solid'"
                        name="offices-selector"
                        :label="$t('expenses.filter.offices')"
                        label-class="form-label"
                        v-model="formData.offices"
                        :placeholder-text="$t('expenses.label.offices_select_creator')"
                      >
                          <option v-for="office in offices" :value="office.id" :key="office.id">
                            {{ office.name }}
                          </option>            
                      </Select2>

                        
                      <SwitchInput v-if="formData.offices.length" id="allUsers" :label="$t('expenses.filter.allUsers')" v-model="formData.allUsers"/>

                      <div v-if="!formData.allUsers" >
                        <Select2
                          :is-required="true"
                          container-class="col-12 mt-2 mb-2"
                          :multiple="true"
                          :show-search="true"
                          :selectClass="'form-select form-control-lg form-select-solid'"
                          name="users-selector"
                          :label="$t('expenses.filter.users')"
                          label-class="form-label"
                          v-model="formData.users"
                          :parent-id="'#'+id"
                          :placeholder-text="$t('expenses.label.users_select_creator')"
                        >
                            <option v-for="user in users" :value="user.id" :key="user.id">
                              {{ user.lastname }} {{ user.firstname }}
                            </option>            
                        </Select2>
                      </div>
                    </div>

                    <div class="separator separator-solid mb-6"></div>
                    <SwitchInput id="createdAt" :label="$t('expenses.filter.createdAt')" v-model="formData.status.createdAt"/>
                    <SwitchInput id="separateFile" :label="$t('expenses.filter.separateFile')" v-model="formData.separateFile"/>
                </div>
            </div>
          </div>
          <div class="modal-footer">
            <Button type="cancel" @click="closeModal" :id="id+'_cancel_btn'" btn-class="btn-sm me-3" size="7px 30px" btn-text="button.cancel"></Button>
            <Button btn-class="btn-sm me-3" type="submit" :id="id+'_submit_btn'" btn-text="button.save" size="7px 30px" :is-loading="isDownloading"></Button>
          </div>
        </div>
      </form>
    </div>
  </div>

</template>

<script setup>
import { computed, ref, defineProps, onMounted } from "vue";
import {useStore} from "vuex";

import Button from "@/components/Button.vue"
import CheckboxInput from "@/components/CheckboxInput.vue";
import SwitchInput from "@/components/SwitchInput.vue";
import InputDate from "@/components/InputDate.vue";
import Select2 from "@/components/Select2.vue";
import CheckboxGroupInput from "@/components/CheckboxGroupInput.vue";
import { useExpenseEnums } from "@/Modules/Financial/composables/expenseEnums";
 
const store = useStore()
const {expenseStatusEnums} = useExpenseEnums()

const props = defineProps({
  id: {
      type: String,
      required: true
  }
})

const STAUTUS_OPTION = [
  {key: expenseStatusEnums.value.PAID, value: i18n.global.t("expenses.status.paid")},
  {key: expenseStatusEnums.value.PARTIALLY_PAID, value: i18n.global.t("expenses.status.partiallyPaid")},
  {key: expenseStatusEnums.value.VALIDATED, value: i18n.global.t("expenses.status.validated")},
  {key: expenseStatusEnums.value.UNVALIDATED, value: i18n.global.t("expenses.status.notValidated")}
]


const formData = ref( 
  {
    status: [],
    startDate: null,
    endDate: null,
    allOffices: true,
    separateFile: true,
    allUsers: true,
    offices: [],
    users: [],
  }
)
const isDownloading = ref(false)

const users = computed(() => store.getters['user/getUsers'])
const offices = computed(() => store.getters['offices/getOffices'])

function closeModal(){
  $('#'+props.id).modal('toggle')
}

function downloadExpenseFile() {
  isDownloading.value = true
  store.dispatch('expenses/expenseFileDownloadRequest', formData.value)
    .then(response => {
      if(response.data.type === 'application/json'){
          window.toastr['info'](i18n.global.t('notification.reports_not_found'))
      }else{
          let blob = new Blob([response.data], { type: response.headers['content-type'] });
          let link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          if(formData.value.separateFile){
              link.download = 'expense file ' + formData.value.startDate.replaceAll('/', '-') + ' to ' + formData.value.endDate.replaceAll('/', '-') +".zip"
          }else{
              link.download = 'expense file ' + formData.value.startDate.replaceAll('/', '-') + ' to ' + formData.value.endDate.replaceAll('/', '-') +".pdf"
          }
          link.click()
          closeModal()
      }

    })
    .catch((err) => {

    })
    .finally(() => isDownloading.value = false)

}



onMounted(() => {
  
})
</script>

<style scoped></style>
